'use client';

import { Button } from '@/components/ui/button';
import { MessageCircle, Phone, MessageSquare } from 'lucide-react';
import { buildWhatsAppUrl } from '@/lib/whatsapp';
import { useCountry } from '@/context/CountryContext';
import Link from 'next/link';

interface ContactButtonsProps {
  phone: string;
  whatsappPhone?: string;
  chatId?: string;
  message?: string;
  variant?: 'default' | 'outline' | 'compact';
  className?: string;
}

/**
 * مكون أزرار التواصل الموحد (واتساب، اتصال، دردشة)
 * يستخدم في كروت السيارات، الورش، وقطع الغيار
 */
export default function ContactButtons({ 
  phone, 
  whatsappPhone, 
  chatId, 
  message = 'السلام عليكم، أنا مهتم بهذا الإعلان عبر تطبيق Maknna.',
  variant = 'default',
  className = ''
}: ContactButtonsProps) {
  const { selectedCountryCode } = useCountry();
  const waUrl = buildWhatsAppUrl(whatsappPhone || phone, message, selectedCountryCode);

  if (variant === 'compact') {
    return (
      <div className={`flex gap-2 ${className}`}>
        <Button size="icon" className="rounded-xl bg-green-500 hover:bg-green-600 text-white shadow-sm" asChild title="واتساب">
          <a href={waUrl} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="h-4 w-4" />
          </a>
        </Button>
        <Button size="icon" variant="outline" className="rounded-xl border-primary text-primary hover:bg-blue-50" asChild title="اتصال">
          <a href={`tel:${phone}`}>
            <Phone className="h-4 w-4" />
          </a>
        </Button>
        {chatId && (
          <Button size="icon" variant="outline" className="rounded-xl border-slate-200" asChild title="دردشة">
            <Link href={`/chat?id=${chatId}`}>
              <MessageSquare className="h-4 w-4" />
            </Link>
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-2 gap-3 ${className}`}>
      <Button 
        className="rounded-2xl h-12 bg-green-500 hover:bg-green-600 text-white flex gap-2 font-bold shadow-md" 
        asChild
      >
        <a href={waUrl} target="_blank" rel="noopener noreferrer">
          <MessageCircle className="h-5 w-5" />
          واتساب
        </a>
      </Button>
      <Button 
        variant="outline" 
        className="rounded-2xl h-12 border-primary text-primary hover:bg-blue-50 flex gap-2 font-bold" 
        asChild
      >
        <a href={`tel:${phone}`}>
          <Phone className="h-5 w-5" />
          اتصال
        </a>
      </Button>
      {chatId && (
        <Button 
          variant="outline" 
          className="col-span-2 rounded-2xl h-12 border-slate-200 text-slate-700 hover:bg-slate-50 flex gap-2 font-bold" 
          asChild
        >
          <Link href={`/chat?id=${chatId}`}>
            <MessageSquare className="h-5 w-5" />
            مراسلة عبر التطبيق
          </Link>
        </Button>
      )}
    </div>
  );
}
